import * as z from "zod";
import {
  billStatusSchema,
  billTypeSchema,
  subscriptionProductSchema,
} from "./get-bills";

const getBillMonthSummariesInput_ = z.object({
  // The month to total, as its start instant (UTC midnight on the 1st) taken
  // verbatim from a returned `months[].monthStart`. Omitted → newest month.
  month: z.date().optional(),
  // Billed-date window to total instead of a whole month ("Custom"). Wins over
  // `month` when both are sent.
  customRange: z.object({ start: z.date(), end: z.date() }).optional(),
  // The invoice table's own filters, so the card totals exactly the rows the
  // merchant is looking at. They scope the selected window, not the month list.
  statuses: z.array(billStatusSchema).optional(),
  products: z.array(subscriptionProductSchema).optional(),
  types: z.array(billTypeSchema).optional(),
  search: z.string().optional(),
});
export const getBillMonthSummariesInput: z.ZodType<GetBillMonthSummariesInput> =
  getBillMonthSummariesInput_;
export interface GetBillMonthSummariesInput extends z.infer<
  typeof getBillMonthSummariesInput_
> {}

const billMonthSchema_ = z.object({
  // UTC month-start instant (midnight on the 1st). Doubles as the stable key
  // the client echoes back as `input.month` to total the month.
  monthStart: z.date(),
});
export const billMonthSchema: z.ZodType<BillMonth> = billMonthSchema_;
export interface BillMonth extends z.infer<typeof billMonthSchema_> {}

const foreignLineSchema_ = z.object({
  currency: z.string(),
  amount: z.number(),
});

// One row per product in the window. A product's figures cover only its own
// bills, so a returns credit is never blended into separately-settled software.
const productBreakdownSchema_ = z.object({
  products: z.array(subscriptionProductSchema),
  invoiceCount: z.number(),
  total: z.number(),
  foreign: z.array(foreignLineSchema_).default([]),
  openingBalance: z.number().default(0),
  hasOpenBill: z.boolean().default(false),
});
export const productBreakdownSchema: z.ZodType<ProductBreakdown> =
  productBreakdownSchema_;
export interface ProductBreakdown extends z.infer<
  typeof productBreakdownSchema_
> {}

const selectedSummarySchema_ = z.object({
  // The totaled window's inclusive bounds — scopes the invoices table with no
  // timezone math, and makes a month and a custom range render identically.
  start: z.date(),
  end: z.date(),
  // Charges in the window, in `currency`. Unstruck foreign charges are not
  // folded in — they carry their own rates and live in `foreign`.
  total: z.number(),
  currency: z.string().default("USD"),
  foreign: z.array(foreignLineSchema_).default([]),
  // Balance carried into the window from the bill before it; negative is credit.
  openingBalance: z.number().default(0),
  breakdown: z.array(productBreakdownSchema_).default([]),
  // Distinct products billed across the window, derived the way the invoices
  // table derives its Product column (a returns bill reads as Returns).
  products: z.array(subscriptionProductSchema),
  invoiceCount: z.number(),
  // The window carries the still-open returns bill, whose total is live and
  // still accruing — the card badges it as in progress.
  hasOpenBill: z.boolean(),
});
export const selectedSummarySchema: z.ZodType<SelectedSummary> =
  selectedSummarySchema_;
export interface SelectedSummary extends z.infer<
  typeof selectedSummarySchema_
> {}

// The account's live returns position, deliberately NOT scoped to the window
// or the filters. Negative is credit. Null when there are no returns bills.
const returnsCreditSchema_ = z.object({
  total: z.number(),
  currency: z.string().default("USD"),
  foreign: z.array(foreignLineSchema_).default([]),
});
export const returnsCreditSchema: z.ZodType<ReturnsCredit> =
  returnsCreditSchema_;
export interface ReturnsCredit extends z.infer<typeof returnsCreditSchema_> {}

const getBillMonthSummariesOutput_ = z.object({
  // Newest month first. Just the picker's option keys — a cheap `bill`-table
  // scan with no money in it.
  months: z.array(billMonthSchema),
  // The one totaled window (`customRange`, else `month`, else newest month). A
  // requested window zeroes rather than nulls; null means no bills at all.
  selected: selectedSummarySchema_.nullable(),
  credit: returnsCreditSchema_.nullable().default(null),
});
export const getBillMonthSummariesOutput: z.ZodType<GetBillMonthSummariesOutput> =
  getBillMonthSummariesOutput_;
export interface GetBillMonthSummariesOutput extends z.infer<
  typeof getBillMonthSummariesOutput_
> {}

export const getBillMonthSummaries = {
  name: "getBillMonthSummaries" as const,
  input: getBillMonthSummariesInput,
  output: getBillMonthSummariesOutput,
};
